/**
 * Build e-Source canvas field specs from extracted PDF/Word text.
 * Handles complex clinical / pharmacy forms: tables, Yes/No, checklists,
 * Initials/Date columns, sign-off rows, and section headers.
 */

import {
  defaultOptionsForType,
  ensureUniqueFieldName,
  isChoiceWithOptions,
  isLayoutField,
  slugifyFieldName,
} from "./esourceFieldTypes";
import { emptyField } from "./esourceService";

const MAX_SPECS = 120;

const NOISE_LINE =
  /^(form labels?|document text|build an e-source|create sensible|mark critical|do not invent|note:|british english|extra instructions|this document is confidential|controlled document|effective date|page\s+\d+(\s+of\s+\d+)?|confidential|\[table\s+\d+\]|panthera|shaping a healthier future)$/i;

const COLUMN_HEADER_ONLY =
  /^(initials\s*\/?\s*date|yes\s*\/?\s*no|yes\s+no|n\/a|comments?|findings?|actions?|description|item|check|response)$/i;

function titleCase(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function codeFromTitle(title) {
  const letters = String(title || "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (letters || "FRM").slice(0, 4) + "01";
}

function normalizeFieldKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function shortLabel(text, max = 42) {
  return String(text || "")
    .replace(/\?$/, "")
    .trim()
    .slice(0, max);
}

function cleanLabel(raw) {
  return String(raw || "")
    .replace(/\s*\|\s*/g, " ")
    .replace(/\bYes\b\s*\[[^\]]*\]\s*\bNo\b\s*\[[^\]]*\]/gi, "")
    .replace(/\bYes\b\s*[☐□\[\]\(\)\s]*\s*\bNo\b\s*[☐□\[\]]*/gi, "")
    .replace(/\bYes\b\s*\bNo\b/gi, "")
    .replace(/\bInitials\s*\/?\s*Date\b/gi, "")
    .replace(/[☐☑☒□■▪◦•]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/[:：]\s*$/, "")
    .trim();
}

function inferFieldType(label) {
  const l = String(label || "");
  if (/date\s*&?\s*time|datetime/i.test(l)) return "datetime";
  if (/\btime\b/i.test(l) && !/initials/i.test(l)) return "time";
  if (/\bdate\b|dob|birth|expiry/i.test(l) && !/initials/i.test(l)) return "date";
  if (/note|comment|description|address|message|remark|detail|finding|action taken|issues identified/i.test(l))
    return "textarea";
  if (/qty|quantity|count|age|score|rating|integer/i.test(l)) return "integer";
  if (/amount|price|cost|total|number|decimal|₹|rs\.?/i.test(l)) return "number";
  if (/yes\s*\/?\s*no|\?$/i.test(l)) return "yesno";
  if (/checkbox|confirm|agree|n\/a\b/i.test(l)) return "checkbox";
  if (/payment|mode|status|priority|gender|type\b|location\b/i.test(l) && /select|choose|option/i.test(l))
    return "dropdown";
  if (/sign(ature)?\b|initials/i.test(l)) return "text";
  return "text";
}

function inferModuleScope(text, fileName) {
  const blob = `${text}\n${fileName}`;
  if (/IMP|pharmacy|accountability|quarantine|kit\b|dispens/i.test(blob)) return "Pharmacy";
  if (/lab\b|sample|specimen|assay|centrifug/i.test(blob)) return "Lab";
  if (/vital|blood pressure|temperature|pulse/i.test(blob)) return "General";
  return "General";
}

function fieldFromSpec(spec, existingFields = []) {
  const type = spec.type || "text";
  const base = emptyField(type);
  const label = spec.label || base.label;
  const field = {
    ...base,
    label,
    required: !!spec.required,
    width: spec.width || base.width,
    placeholder: spec.placeholder || "",
    helpText: spec.helpText || "",
    defaultValue: spec.defaultValue ?? base.defaultValue,
    maxLength: spec.maxLength ?? "",
    minLength: spec.minLength ?? "",
    pattern: spec.pattern ?? "",
    minValue: spec.minValue ?? "",
    maxValue: spec.maxValue ?? "",
    dontAllowFuture: !!spec.dontAllowFuture,
    dontAllowPast: !!spec.dontAllowPast,
    defaultCurrentDate: !!spec.defaultCurrentDate,
    optionsLayout: spec.optionsLayout || base.optionsLayout || "stacked",
    options: isChoiceWithOptions(type)
      ? spec.options || defaultOptionsForType(type)
      : undefined,
  };
  field.fieldName = ensureUniqueFieldName(
    slugifyFieldName(label),
    existingFields,
    field.id,
  );
  return field;
}

function mapSpecsWithUniqueNames(specs) {
  const out = [];
  (specs || []).forEach((spec) => {
    out.push(fieldFromSpec(spec, out));
  });
  return out;
}

function splitCells(line) {
  return String(line || "")
    .split(/\s*\|\s*/)
    .map((c) => c.trim())
    .filter(Boolean);
}

function isSectionHeader(line) {
  const head = cleanLabel(splitCells(line)[0] || line);
  if (!head || head.length > 72) return false;
  if (/\?/.test(head)) return false;
  if (COLUMN_HEADER_ONLY.test(head)) return false;
  if (/\bYes\b/i.test(head) && /\bNo\b/i.test(head)) return false;
  if (/[.!]$/.test(head) && head.split(/\s+/).length > 8) return false;

  // Single short title, Title Case / ALL CAPS / ends with "Checks|Details|Approval"
  if (
    /^(Safety|Accountability|Review|Approval|Issues|Identification|Details|Product|Sign-?off|Shipment|Collection|Measurement|Event|Reporter|About|Feedback|Personal|Order|Customer)\b/i.test(
      head,
    )
  ) {
    return true;
  }
  if (
    /\b(Checks?|Details|Approval|Review|Actions?\s+Taken|Information|Log)\s*$/i.test(
      head,
    ) &&
    head.split(/\s+/).length <= 8
  ) {
    return true;
  }
  // ALL CAPS short header
  if (head.length <= 48 && head === head.toUpperCase() && /[A-Z]/.test(head) && head.split(/\s+/).length <= 6) {
    return true;
  }
  return false;
}

function looksLikeYesNoRow(line) {
  const l = String(line || "");
  if (/\bYes\b/i.test(l) && /\bNo\b/i.test(l)) return true;
  if (/\?\s*$/.test(cleanLabel(splitCells(l)[0] || l)) && /initials/i.test(l))
    return true;
  return false;
}

function looksLikeSignOff(line) {
  const l = String(line || "");
  return /\bSign(ature)?\b/i.test(l) && /\bDate\b/i.test(l);
}

function looksLikeCheckboxItem(line) {
  const l = String(line || "").trim();
  if (/^[☐☑☒□■▪◦•\-\*]\s+\S/.test(l)) return true;
  if (/^\[\s*\]\s+\S/.test(l)) return true;
  if (/^n\/a\b/i.test(l) && l.length < 80) return true;
  // Short action phrases common under "If yes, ensure…"
  if (
    l.length >= 8 &&
    l.length <= 100 &&
    !/\?/.test(l) &&
    !/\bYes\b/i.test(l) &&
    /^(Quarantine|Complete|Print|Replace|Update|Notify|File|Destroy|Return|Record|Ensure|Raise|Attach|Send)\b/i.test(
      l,
    )
  ) {
    return true;
  }
  return false;
}

function looksLikeInstruction(line) {
  const l = cleanLabel(line);
  if (l.length < 40) return false;
  if (/\?$/.test(l)) return false;
  const words = l.split(/\s+/).length;
  if (words >= 12 && /[.!]$/.test(l)) return true;
  if (words >= 16) return true;
  if (/^(check|ensure|if\s|for any|please\s)/i.test(l) && words >= 10) return true;
  return false;
}

function guessFormName(fileName, body) {
  const fromFile = String(fileName || "")
    .replace(/\.(pdf|docx?)$/i, "")
    .replace(/^[A-Z0-9]+\.[0-9]+\s*,?\s*/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
  if (fromFile.length >= 4) return titleCase(fromFile).slice(0, 80);

  const firstUseful = String(body || "")
    .split(/\n+/)
    .map((l) => cleanLabel(l))
    .find(
      (l) =>
        l.length >= 8 &&
        l.length <= 90 &&
        !NOISE_LINE.test(l) &&
        !COLUMN_HEADER_ONLY.test(l) &&
        !/^\[Table/i.test(l),
    );
  return titleCase(firstUseful || "Imported form").slice(0, 80);
}

/**
 * Parse extracted document text into field specs (complex-form aware).
 * @returns {{ specs: object[], name: string, moduleScope: string, summary: string } | null}
 */
export function buildFormSpecsFromDocumentText({ body, fileName }) {
  const text = String(body || "");
  const name = guessFormName(fileName, text);
  const moduleScope = inferModuleScope(text, fileName || "");

  const lines = text
    .split(/\n+/)
    .map((l) => l.replace(/^[\s•\-–—*]+/, "").trim())
    .filter(Boolean);

  const specs = [];
  const seen = new Set();
  let initialsCounter = 0;
  let lastYesNoLabel = "";

  const addSpec = (spec) => {
    if (!spec?.label || specs.length >= MAX_SPECS) return false;
    let key = normalizeFieldKey(spec.label);
    if (!key) return false;
    // Allow repeated Initials/Date by appending counter into uniqueness key
    if (/^initials\s*\/?\s*date/i.test(spec.label) || /— initials/i.test(spec.label)) {
      initialsCounter += 1;
      key = `${key} ${initialsCounter}`;
      if (!/\(\d+\)|—/.test(spec.label) && initialsCounter > 1) {
        spec = {
          ...spec,
          label: `${spec.label} (${initialsCounter})`,
        };
        key = normalizeFieldKey(spec.label);
      }
    }
    if (seen.has(key)) return false;
    seen.add(key);
    specs.push(spec);
    return true;
  };

  const addInitialsDate = (contextLabel) => {
    const ctx = shortLabel(contextLabel || "check", 36);
    addSpec({
      type: "text",
      label: `Initials / date — ${ctx}`,
      required: true,
      width: "half",
    });
  };

  for (const rawLine of lines) {
    if (NOISE_LINE.test(rawLine)) continue;
    if (/^https?:\/\//i.test(rawLine)) continue;

    const cells = splitCells(rawLine);
    const firstCell = cleanLabel(cells[0] || rawLine);

    // Column header row
    if (
      cells.length >= 1 &&
      cells.every((c) => COLUMN_HEADER_ONLY.test(cleanLabel(c)) || cleanLabel(c).length < 3)
    ) {
      continue;
    }

    if (isSectionHeader(rawLine)) {
      addSpec({ type: "section", label: firstCell.slice(0, 80) });
      lastYesNoLabel = "";
      continue;
    }

    if (looksLikeSignOff(rawLine)) {
      const who =
        cleanLabel(
          firstCell
            .replace(/\bSign(ature)?\b.*$/i, "")
            .replace(/\bDate\b.*$/i, ""),
        ) || "Reviewer";
      addSpec({
        type: "text",
        label: `${who} — sign`,
        required: true,
        width: "half",
      });
      addSpec({
        type: "date",
        label: `${who} — date`,
        required: true,
        width: "half",
        defaultCurrentDate: true,
      });
      continue;
    }

    if (looksLikeYesNoRow(rawLine)) {
      let q = firstCell;
      if (!q || q.length < 6) q = cleanLabel(rawLine);
      q = cleanLabel(q);
      if (q.length >= 6 && q.length <= 200) {
        const qLabel = /\?$/.test(q) ? q : /^(have|has|is|are|was|were|do|does|did|any)\b/i.test(q) ? `${q}?` : q;
        addSpec({
          type: "yesno",
          label: qLabel,
          required: true,
          width: "half",
        });
        lastYesNoLabel = qLabel;
        if (/initials/i.test(rawLine) || cells.some((c) => /initials/i.test(c))) {
          addInitialsDate(qLabel);
        } else {
          // Compliance tables almost always have Initials/Date column
          addInitialsDate(qLabel);
        }
      }
      continue;
    }

    if (looksLikeCheckboxItem(rawLine)) {
      const label = cleanLabel(rawLine).replace(/^n\/a\s*[—\-:]?\s*/i, (m) =>
        /n\/a/i.test(m) ? "N/A — " : "",
      );
      if (label.length >= 4) {
        addSpec({
          type: "checkbox",
          label: label.slice(0, 160),
          required: false,
          width: "half",
        });
      }
      continue;
    }

    // Multi-cell label | blank | Initials  → text/instruction + initials (before long-instruction skip)
    if (cells.length >= 2) {
      const label = cleanLabel(cells[0]);
      const rest = cells.slice(1).join(" ");
      if (label && !COLUMN_HEADER_ONLY.test(label) && label.length <= 280) {
        if (/initials/i.test(rest) || cells.some((c) => /^initials/i.test(cleanLabel(c)))) {
          if (looksLikeInstruction(label) || label.length > 120) {
            addSpec({
              type: "instruction",
              label: label.slice(0, 280),
            });
            addInitialsDate(shortLabel(label, 28) || "check");
          } else {
            const type = inferFieldType(label);
            addSpec({
              type: type === "yesno" ? "text" : type,
              label: label.slice(0, 160),
              required: /location|name|id|date|batch|lot|sign/i.test(label),
              width: "half",
            });
            addInitialsDate(label);
          }
          continue;
        }
        // Two meaningful labels side by side
        if (
          cells.length === 2 &&
          cleanLabel(cells[1]).length >= 3 &&
          !COLUMN_HEADER_ONLY.test(cleanLabel(cells[1])) &&
          !looksLikeInstruction(label)
        ) {
          const second = cleanLabel(cells[1]);
          if (second.length <= 80 && !looksLikeInstruction(second)) {
            [label, second].forEach((lab) => {
              const type = inferFieldType(lab);
              addSpec({
                type,
                label: lab,
                required: /id|date|name|location|batch|lot|expiry|quantity|sign|initials/i.test(lab),
                width: type === "textarea" ? "full" : "half",
              });
            });
            continue;
          }
        }
      }
    }

    if (looksLikeInstruction(rawLine)) {
      addSpec({
        type: "instruction",
        label: cleanLabel(rawLine).slice(0, 280),
      });
      continue;
    }

    // Plain label line
    let label = cleanLabel(rawLine);
    if (label.length < 2 || label.length > 160) continue;
    if (COLUMN_HEADER_ONLY.test(label)) continue;

    const type = inferFieldType(label);
    const required =
      /id|date|name|location|batch|lot|expiry|quantity|performed|received|sign|initials/i.test(
        label,
      ) && !/comment|note|remark|optional|instruction|finding/i.test(label);

    addSpec({
      type,
      label,
      required,
      width: type === "textarea" || type === "section" ? "full" : "half",
    });

    // After a long question without Yes/No markers, still add initials if line mentions it
    if (/initials/i.test(rawLine) && type !== "text") {
      addInitialsDate(label);
    }
  }

  // Drop leading instructions-only noise if we have real fields
  const inputCount = specs.filter((s) => !isLayoutField(s.type)).length;
  if (inputCount < 2) return null;

  // Soft-trim trailing pure instructions beyond a useful canvas
  let trimmed = specs;
  if (specs.length > MAX_SPECS) trimmed = specs.slice(0, MAX_SPECS);

  return {
    specs: trimmed,
    name,
    moduleScope,
    summary: `Generated ${trimmed.length} field(s) from “${fileName || "document"}” (tables, Yes/No, checklists and sign-off rows mapped where detected). Review the canvas before saving.`,
  };
}

/**
 * Known pharmacy monthly IMP compliance pack — used only when structural parse is thin
 * but the file clearly matches this controlled document.
 */
export function buildImpComplianceFallbackSpecs(formNameGuess, fileName) {
  const specs = [
    { type: "section", label: "Study / location" },
    { type: "text", label: "Location", required: true, width: "half" },
    { type: "text", label: "Initials / date (location)", required: true, width: "half" },
    {
      type: "textarea",
      label: "IMP location log check notes (moved since last accountability check?)",
      required: false,
    },
    { type: "text", label: "Initials / date (location log)", required: false, width: "half" },
    { type: "section", label: "Safety Checks" },
    { type: "yesno", label: "Have any IMP kits passed expiry date?", required: true, width: "half" },
    { type: "text", label: "Initials / date (expiry check)", required: true, width: "half" },
    { type: "checkbox", label: "N/A — expiry follow-up actions", required: false, width: "half" },
    { type: "checkbox", label: "Quarantine expired IMP", required: false, width: "half" },
    { type: "checkbox", label: "Complete part 1 of TC07D (or equivalent)", required: false, width: "half" },
    { type: "checkbox", label: "Notify study team / pharmacy as required", required: false, width: "half" },
    { type: "yesno", label: "Has there been any quarantined stock released?", required: true, width: "half" },
    { type: "text", label: "Initials / date (quarantine release)", required: true, width: "half" },
    { type: "checkbox", label: "N/A — quarantine release actions", required: false, width: "half" },
    { type: "checkbox", label: "Print sponsor IMP release email", required: false, width: "half" },
    { type: "checkbox", label: "Replace IMP in correct storage location", required: false, width: "half" },
    { type: "checkbox", label: "Update accountability / location log", required: false, width: "half" },
    { type: "section", label: "Accountability Checks" },
  ];

  [
    "QC Individual Subject and Master / Combined Accountability Logs",
    "Returned / used IMP reconciliation",
    "Temperature excursions",
    "Unused IMP returned for destruction",
    "Non-IMP accountability and expiry check",
    "Quality issue raised",
  ].forEach((q, idx) => {
    specs.push({ type: "yesno", label: q, required: true, width: "half" });
    specs.push({ type: "text", label: `Initials / date (${idx + 1})`, required: true, width: "half" });
    specs.push({ type: "textarea", label: `Findings / notes (${shortLabel(q)})`, required: false });
  });

  specs.push(
    { type: "section", label: "Review and approval" },
    { type: "text", label: "Study Coordinator review — sign", required: true, width: "half" },
    { type: "date", label: "Study Coordinator review — date", required: true, width: "half", defaultCurrentDate: true },
    { type: "text", label: "Principal Investigator / Sub-I approval — sign", required: true, width: "half" },
    { type: "date", label: "Principal Investigator / Sub-I approval — date", required: true, width: "half" },
    { type: "section", label: "Issues identified and actions taken" },
    { type: "textarea", label: "Issues identified and actions taken", required: false },
    { type: "text", label: "Initials / date (issues)", required: false, width: "half" },
  );

  const name =
    /IMP\s+Compliance/i.test(fileName || "") || /Monthly\s+Study/i.test(formNameGuess || "")
      ? "Monthly Study Specific IMP Compliance Check"
      : formNameGuess || "IMP Compliance Check";

  return {
    specs,
    name,
    moduleScope: "Pharmacy",
    summary: `Generated a pharmacy IMP compliance checklist form (${specs.length} fields) from “${fileName || "document"}”. Review Yes/No items and initials fields before saving.`,
  };
}

export function looksLikeImpComplianceDocument(body, fileName, formNameGuess) {
  const text = `${body || ""}\n${fileName || ""}\n${formNameGuess || ""}`;
  return /IMP\s+Compliance|Accountability\s+Checks|Safety\s+Checks|quarantined\s+stock|IMP\s+kits?\s+passed\s+expiry|Monthly\s+Study\s+Specific|CP07B/i.test(
    text,
  );
}

/**
 * Full pipeline used by AQ Assist local document import.
 */
export function buildCanvasFromUploadedDocument({ prompt }) {
  const raw = String(prompt || "");
  const marker = raw.search(/Document content:\s*/i);
  const body =
    marker >= 0 ? raw.slice(marker).replace(/^Document content:\s*/i, "") : raw;
  const fileMatch = raw.match(/uploaded document\s*\([“"]([^”"]+)[”"]\)/i);
  const fileName = fileMatch?.[1] || "";
  const formNameGuess = guessFormName(fileName, body);

  const structural = buildFormSpecsFromDocumentText({ body, fileName });
  const structuralInputs = structural
    ? structural.specs.filter((s) => !isLayoutField(s.type)).length
    : 0;

  let chosen = structural;
  if (
    looksLikeImpComplianceDocument(body, fileName, formNameGuess) &&
    structuralInputs < 8
  ) {
    chosen = buildImpComplianceFallbackSpecs(formNameGuess, fileName);
  }

  if (!chosen || !chosen.specs?.length) {
    return {
      fields: [],
      blocked: false,
      replaceCanvas: false,
      summary:
        "I could not detect enough field labels in that document. Try a clearer form PDF/Word file, or describe the fields in chat.",
    };
  }

  return {
    fields: mapSpecsWithUniqueNames(chosen.specs),
    code: codeFromTitle(chosen.name),
    name: chosen.name,
    moduleScope: chosen.moduleScope || "General",
    blocked: false,
    replaceCanvas: true,
    summary: chosen.summary,
  };
}
